import { createContext, FC, useEffect, useMemo } from 'react';
import { Prompt } from '@ai-platform/utils';
import { createVisibilityTracker, VisibilityTracker } from '../VisibilityTracker';
import { useMarkAsViewed } from './useMarkAsViewed';
import { usePlayVideo } from './usePlayVideo';
// import { useDebugOutline } from './useDebugOutline';

export type TrackerPromptData = {
    prompt: Prompt;
    needMarkAsViewed?: boolean;
    videoRef?: React.RefObject<HTMLVideoElement | null>;
};
export const PromptsViewedTrackerContext = createContext<VisibilityTracker<TrackerPromptData> | null>(null);

type Props = {
    children: React.ReactNode;
};

const PROMPT_CARD_VIEWED_THRESHOLD = 0.5; // считаем промпт в ленте видимым, если на экране половина карточки
const VIEWED_DELAY = 2000; // считаем промпт просмотренным, если он был видимым в течение 2 секунд
const tracker = createVisibilityTracker<TrackerPromptData>(PROMPT_CARD_VIEWED_THRESHOLD);

export const PromptsViewedTrackerProvider: FC<Props> = ({ children }) => {
    // const [outlineVisible, outlineHidden] = useDebugOutline();
    const [markVisiblePromptAsViewed] = useMarkAsViewed();
    const [handleVideoShow, handleVideoHide] = usePlayVideo();

    // список обработчиков показанных промптов
    const shownHandlers = useMemo(
        () =>
            [
                // [outlineVisible, 0],
                [markVisiblePromptAsViewed, VIEWED_DELAY],
                [handleVideoShow, 0], // запускаем видео сразу при показе
            ] as const,
        [
            // outlineVisible,
            markVisiblePromptAsViewed,
            handleVideoShow,
        ],
    );

    // список обработчиков скрытых промптов
    const hiddenHandlers = useMemo(
        () =>
            [
                // [outlineHidden, 0],
                [handleVideoHide, 0], // останавливаем видео сразу при скрытии
            ] as const,
        [
            // outlineHidden,
            handleVideoHide,
        ],
    );

    useEffect(() => {
        // в случае SSR обсервер создать не удастся и регистрировать обработчики не нужно
        if (!tracker) {
            return;
        }

        // регистрируем обработчики показанных промптов
        for (const [handler, delay] of shownHandlers) {
            tracker.addHandler('shown', handler, delay);
        }

        // регистрируем обработчики скрытых промптов
        for (const [handler, delay] of hiddenHandlers) {
            tracker.addHandler('hidden', handler, delay);
        }

        return () => {
            // удаляем обработчики
            for (const [handler] of shownHandlers) {
                tracker.removeHandler(handler);
            }
            for (const [handler] of hiddenHandlers) {
                tracker.removeHandler(handler);
            }
        };
    }, [hiddenHandlers, shownHandlers]);

    return <PromptsViewedTrackerContext.Provider value={tracker}>{children}</PromptsViewedTrackerContext.Provider>;
};
